// packages/web/src/components/admin/uren-edit/entry-reject-modal.tsx
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useTransitionEntry } from "@/api/hours-entries/mutations";
import { ResponsiveDialog, ResponsiveDialogFooter } from "@/components/responsive-dialog";
import { Button } from "@/components/ui/button";

interface EntryRejectModalProps {
  open: boolean;
  entryId: string;
  onClose: () => void;
  onRejected: () => void;
}

export function EntryRejectModal({ open, entryId, onClose, onRejected }: EntryRejectModalProps) {
  const { t } = useTranslation();
  const transition = useTransitionEntry();

  const schema = z.object({
    comment: z
      .string()
      .trim()
      .min(1, { message: t("admin.uren.reject-modal.comment-placeholder") }),
  });

  const form = useForm({
    defaultValues: { comment: "" },
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      const trimmed = value.comment.trim();
      try {
        await transition.mutateAsync({
          id: entryId,
          body: { target: "rejected", comment: trimmed },
        });
        toast.success(t("admin.uren.toasts.saved"));
        form.reset();
        onRejected();
      } catch (err) {
        toast.error((err as Error).message);
      }
    },
  });

  useEffect(() => {
    if (!open) form.reset();
  }, [open, form]);

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("admin.uren.reject-modal.title")}
      description={t("admin.uren.reject-modal.body")}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.Field name="comment">
          {(field) => {
            const error = field.state.meta.errors[0];
            const errorText = typeof error === "string" ? error : error?.message;
            return (
              <>
                <textarea
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t("admin.uren.reject-modal.comment-placeholder")}
                  className={`w-full rounded border ${errorText ? "border-vroom-red" : "border-line"} bg-surface p-2 text-sm min-h-[96px]`}
                  aria-label="Reden"
                  aria-invalid={!!errorText}
                />
                {errorText && <p className="mt-1 text-xs text-vroom-red">{errorText}</p>}
              </>
            );
          }}
        </form.Field>
        <ResponsiveDialogFooter>
          {/* Annuleren PRIMARY per destructive-confirm convention */}
          <Button type="submit" variant="danger-soft" disabled={transition.isPending}>
            {t("admin.uren.reject-modal.confirm")}
          </Button>
          <Button type="button" variant="primary" onClick={onClose} autoFocus>
            {t("admin.uren.reject-modal.cancel")}
          </Button>
        </ResponsiveDialogFooter>
      </form>
    </ResponsiveDialog>
  );
}
